import { Injectable } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { TranslatorService } from '@sc/data-access-translation';
import { ErrorMessage, PatternError } from '@sc/data-access-models';

@Injectable({
  providedIn: 'root',
})
export class ErrorMessagesService {
  constructor(private readonly translatorService: TranslatorService) {}

  getError(
    abstractControl: AbstractControl,
    fieldName: string,
    errorPattern?: PatternError
  ): ErrorMessage {
    const formControl = abstractControl as FormControl;
    if (formControl.hasError('required')) {
      if (errorPattern === 'requiredCompanyName') {
        return {
          title: this.translatorService.getInstantTranslation(
            'MatErrors.requiredCompanyNameErrorTitle'
          ),
          message: this.translatorService.getInstantTranslation(
            'MatErrors.requiredCompanyNameErrorMessage'
          ),
        };
      }
      return {
        title: this.translatorService.getInstantTranslation(
          'MatErrors.requiredErrorTitle'
        ),
        message: this.translatorService.getInstantTranslation(
          'MatErrors.requiredErrorMessage',
          { fieldName: fieldName }
        ),
      };
    }

    if (formControl.hasError('maxlength')) {
      return {
        title: this.translatorService.getInstantTranslation(
          'MatErrors.maxLengthErrorTitle'
        ),
        message: this.translatorService.getInstantTranslation(
          'MatErrors.maxLengthErrorMessage',
          {
            fieldName: fieldName,
            maxLength: formControl?.errors?.maxlength?.requiredLength ?? 100,
          }
        ),
      };
    }

    if (formControl.hasError('license_plate_error')) {
      return {
        title: this.translatorService.getInstantTranslation(
          'MatErrors.licensePlateErrorTitle'
        ),
        message: this.translatorService.getInstantTranslation(
          'MatErrors.licensePlateErrorMessage'
        ),
      };
    }

    if (formControl.hasError('name_already_defined')) {
      return {
        title: this.translatorService.getInstantTranslation(
          'MatErrors.nameAlreadyDefinedErrorTitle'
        ),
        message: this.translatorService.getInstantTranslation(
          'MatErrors.nameAlreadyDefinedErrorMessage'
        ),
      };
    }

    if (formControl.hasError('license_plate_already_defined')) {
      return {
        title: this.translatorService.getInstantTranslation(
          'MatErrors.licensePlateAlreadyDefinedErrorTitle'
        ),
        message: this.translatorService.getInstantTranslation(
          'MatErrors.licensePlateAlreadyDefinedErrorMessage'
        ),
      };
    }

    if (formControl.hasError('pattern') && errorPattern) {
      if (errorPattern === 'emailPattern') {
        return {
          title: this.translatorService.getInstantTranslation(
            'MatErrors.emailErrorTitle'
          ),
          message: this.translatorService.getInstantTranslation(
            'MatErrors.emailErrorMessage'
          ),
        };
      }

      if (errorPattern === 'alphaNumPattern') {
        return {
          title: this.translatorService.getInstantTranslation(
            'MatErrors.alphaNumPatternErrorTitle'
          ),
          message: this.translatorService.getInstantTranslation(
            'MatErrors.alphaNumPatternErrorMessage',
            { fieldName: fieldName }
          ),
        };
      }

      if (errorPattern === 'phoneNumberPattern') {
        return {
          title: this.translatorService.getInstantTranslation(
            'MatErrors.phoneNumberPatternErrorTitle'
          ),
          message: this.translatorService.getInstantTranslation(
            'MatErrors.phoneNumberPatternErrorMessage'
          ),
        };
      }

      if (errorPattern === 'datePattern') {
        return {
          title: this.translatorService.getInstantTranslation(
            'MatErrors.datePatternErrorTitle'
          ),
          message: this.translatorService.getInstantTranslation(
            'MatErrors.datePatternErrorMessage'
          ),
        };
      }

      if (errorPattern === 'hourPattern') {
        return {
          title: this.translatorService.getInstantTranslation(
            'MatErrors.hourPatternErrorTitle'
          ),
          message: this.translatorService.getInstantTranslation(
            'MatErrors.hourPatternErrorMessage'
          ),
        };
      }

      if (errorPattern === 'siretPattern') {
        return {
          title: this.translatorService.getInstantTranslation(
            'MatErrors.specificLengthErrorTitle'
          ),
          message: this.translatorService.getInstantTranslation(
            'MatErrors.specificLengthErrorMessage',
            {
              fieldName: fieldName,
              length: 14,
            }
          ),
        };
      }
    }

    if (formControl.hasError('contact_field_already_defined')) {
      return {
        title: this.translatorService.getInstantTranslation(
          'MatErrors.contactFieldAlreadyDefinedErrorTitle',
          {
            fieldName: fieldName,
          }
        ),
        message: this.translatorService.getInstantTranslation(
          'MatErrors.contactFieldAlreadyDefinedErrorMessage',
          { fieldName: fieldName.toLowerCase() }
        ),
      };
    }

    if (formControl.hasError('resource_name_already_defined')) {
      return {
        title: this.translatorService.getInstantTranslation(
          'MatErrors.resourceNameAlreadyDefinedErrorTitle'
        ),
        message: this.translatorService.getInstantTranslation(
          'MatErrors.resourceNameAlreadyDefinedErrorMessage'
        ),
      };
    }

    if (formControl.hasError('intervention_vehicle_name_already_defined')) {
      return {
        title: this.translatorService.getInstantTranslation(
          'MatErrors.interventionVehicleNameAlreadyDefinedErrorTitle'
        ),
        message: this.translatorService.getInstantTranslation(
          'MatErrors.interventionVehicleNameAlreadyDefinedErrorMessage'
        ),
      };
    }

    if (formControl.hasError('manager_email_already_defined')) {
      return {
        title: this.translatorService.getInstantTranslation(
          'MatErrors.managerEmailAlreadyDefinedErrorTitle'
        ),
        message: this.translatorService.getInstantTranslation(
          'MatErrors.managerEmailAlreadyDefinedErrorMessage'
        ),
      };
    }

    if (formControl.hasError('type_intervention_already_defined')) {
      return {
        title: this.translatorService.getInstantTranslation(
          'MatErrors.typeInterventionAlreadyDefinedErrorTitle'
        ),
        message: this.translatorService.getInstantTranslation(
          'MatErrors.typeInterventionAlreadyDefinedErrorMessage'
        ),
      };
    }

    if (formControl.hasError('authentication_failed')) {
      return {
        title: this.translatorService.getInstantTranslation(
          'MatErrors.loginErrorTitle'
        ),
        message: this.translatorService.getInstantTranslation(
          'MatErrors.loginErrorMessage'
        ),
      };
    }

    if (formControl.hasError('address_not_in_suggestion')) {
      return {
        title: this.translatorService.getInstantTranslation(
          'MatErrors.addressSuggestionErrorTitle'
        ),
        message: this.translatorService.getInstantTranslation(
          'MatErrors.addressSuggestionErrorMessage'
        ),
      };
    }

    if (formControl.hasError('no_recurrence_end_date')) {
      return {
        title: this.translatorService.getInstantTranslation(
          'MatErrors.noRecurrenceEndDateErrorTitle'
        ),
        message: this.translatorService.getInstantTranslation(
          'MatErrors.noRecurrenceEndDateErrorMessage'
        ),
      };
    }

    if (formControl.hasError('location_without_placeid')) {
      return {
        title: this.translatorService.getInstantTranslation(
          'MatErrors.locationWithoutPlaceIdErrorTitle'
        ),
        message: this.translatorService.getInstantTranslation(
          'MatErrors.locationWithoutPlaceIdErrorMessage'
        ),
      };
    }

    return null;
  }

  isTouchedFormControl(abstractControl: AbstractControl): boolean {
    const formControl = abstractControl as FormControl;
    return formControl.touched || formControl.dirty;
  }

  isInvalidFormControl(abstractControl: AbstractControl): boolean {
    const formControl = abstractControl as FormControl;
    return this.isTouchedFormControl(formControl) && formControl.invalid;
  }
}
